/**
 * CSV parsing, validation, and cost-table diff utilities for the cost upload flow.
 * Used by CostUploadDialog.
 */

export interface CSVData {
  headers: string[];
  rows: Record<string, unknown>[];
  fileName: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  data?: CSVData;
}

export interface ColumnDefinition {
  header: string;
  dataType: 'string' | 'number' | 'integer' | 'boolean' | 'date';
  required?: boolean;
  allowEmpty?: boolean;
  /** Human-readable label shown in the upload dialog's expected-format hint. Omit to hide the column from the hint. */
  displayName?: string;
}

/** Row shape for cost table data used in CSV update diffing. */
export interface CostTableRowForUpload {
  id: string;
  assetGroupSet: string;
  description: string;
  assetCount: number;
  unitCost: number;
  [key: string]: unknown;
}

/**
 * Validates that a string value matches the expected data type.
 */
export function validateDataType(
  value: string,
  dataType: ColumnDefinition['dataType']
): boolean {
  if (!value || value.trim() === '') {
    return false;
  }

  switch (dataType) {
    case 'string':
      return typeof value === 'string';
    case 'number':
      return !isNaN(parseFloat(value)) && isFinite(parseFloat(value));
    case 'integer':
      return (
        !isNaN(parseInt(value, 10)) &&
        parseInt(value, 10).toString() === value.trim()
      );
    case 'boolean':
      return ['true', 'false', '1', '0', 'yes', 'no'].includes(
        value.toLowerCase()
      );
    case 'date': {
      const dateValue = new Date(value);
      return (
        !isNaN(dateValue.getTime()) && dateValue.toString() !== 'Invalid Date'
      );
    }
    default:
      return true;
  }
}

/**
 * Returns a human-readable label for a column data type.
 */
export function getDataTypeDisplayName(
  dataType: ColumnDefinition['dataType']
): string {
  switch (dataType) {
    case 'string':
      return 'Text';
    case 'number':
      return 'Number';
    case 'integer':
      return 'Integer';
    case 'boolean':
      return 'Boolean (true/false, 1/0, yes/no)';
    case 'date':
      return 'Date';
    default:
      return dataType;
  }
}

/**
 * Compares CSV rows to existing cost table data and returns a map of updates
 * (keyed by keyField) for rows that have changes in comparable fields.
 */
export function generateUpdatesFromCSV(
  csvData: CSVData,
  existingData: CostTableRowForUpload[],
  keyField: string,
  comparableFields: string[]
): Record<string, CostTableRowForUpload> {
  const updates: Record<string, CostTableRowForUpload> = {};

  if (!existingData || comparableFields.length === 0) {
    return updates;
  }

  const existingDataMap = new Map<string, CostTableRowForUpload>();
  existingData.forEach(row => {
    const key = String(row[keyField]);
    existingDataMap.set(key, row);
  });

  csvData.rows.forEach(csvRow => {
    const csvKey = String(csvRow[keyField] || '');
    const existingRow = existingDataMap.get(csvKey);

    if (existingRow) {
      let hasChanges = false;
      const updatedRow: CostTableRowForUpload = {
        id: existingRow.id,
        assetGroupSet: existingRow.assetGroupSet,
        description: existingRow.description,
        assetCount: existingRow.assetCount,
        unitCost: existingRow.unitCost,
      };

      comparableFields.forEach(field => {
        const csvValueRaw = csvRow[field];
        let csvValue: unknown = csvValueRaw;

        if (field === 'unitCost' || field === 'assetCount') {
          csvValue = Number(csvValueRaw);
        } else {
          csvValue = String(csvValueRaw || '');
        }

        const existingValue = existingRow[field as keyof CostTableRowForUpload];

        if (
          csvValue !== existingValue &&
          csvValueRaw !== null &&
          csvValueRaw !== undefined &&
          csvValueRaw !== ''
        ) {
          updatedRow[field as keyof CostTableRowForUpload] = csvValue;
          hasChanges = true;
        }
      });

      if (hasChanges) {
        updates[csvKey] = updatedRow;
      }
    }
  });

  return updates;
}

/**
 * Validates CSV structure: required columns, optional column restrictions,
 * empty rows, and per-cell data types.
 */
export function validateCSVStructure(
  data: Record<string, unknown>[],
  fileName: string,
  expectedColumns?: ColumnDefinition[],
  allowAdditionalColumns = true
): ValidationResult {
  const errors: string[] = [];

  if (!data || data.length === 0) {
    errors.push('CSV file is empty');
    return { isValid: false, errors };
  }

  const headers = Object.keys(data[0]);

  const emptyHeaders = headers.filter(
    (header: string) => !header || (header as string).trim() === ''
  );
  if (emptyHeaders.length > 0) {
    errors.push('CSV contains empty column headers');
  }

  const columnsToValidate = expectedColumns || [];

  if (columnsToValidate.length > 0) {
    const requiredColumns = columnsToValidate.filter(
      (col: ColumnDefinition) => col.required !== false
    );
    const missingHeaders = requiredColumns
      .map((col: ColumnDefinition) => col.header)
      .filter((expectedHeader: string) => !headers.includes(expectedHeader));

    if (missingHeaders.length > 0) {
      errors.push(`Missing required columns: ${missingHeaders.join(', ')}`);
    }

    if (!allowAdditionalColumns) {
      const expectedHeaderNames = columnsToValidate.map(
        (col: ColumnDefinition) => col.header
      );
      const unexpectedHeaders = headers.filter(
        (header: string) => !expectedHeaderNames.includes(header)
      );
      if (unexpectedHeaders.length > 0) {
        errors.push(
          `Unexpected columns found: ${unexpectedHeaders.join(', ')}`
        );
      }
    }
  }

  data.forEach((row, index) => {
    const rowNumber = index + 2;

    const values = Object.values(row);
    const nonEmptyValues = values.filter(
      val => val !== null && val !== undefined && val !== ''
    );

    if (nonEmptyValues.length === 0) {
      errors.push(`Row ${rowNumber} is completely empty`);
    }

    if (columnsToValidate.length > 0) {
      columnsToValidate.forEach((colDef: ColumnDefinition) => {
        const value = row[colDef.header];
        const isEmpty = value === null || value === undefined || value === '';

        if (isEmpty && !colDef.allowEmpty && colDef.required !== false) {
          errors.push(
            `Row ${rowNumber}, Column "${colDef.header}" is required but empty`
          );
        }

        if (!isEmpty && !validateDataType(String(value), colDef.dataType)) {
          errors.push(
            `Row ${rowNumber}, Column "${
              colDef.header
            }" has invalid data type. Expected: ${getDataTypeDisplayName(
              colDef.dataType
            )}, Got: "${value}"`
          );
        }
      });
    }
  });

  const csvData: CSVData = {
    headers,
    rows: data,
    fileName,
  };

  return {
    isValid: errors.length === 0,
    errors,
    data: csvData,
  };
}

/**
 * Builds a drag-state class name string for the dropzone UI.
 */
export function getDragClassName(
  isDragActive: boolean,
  isDragAccept: boolean,
  isDragReject: boolean
): string {
  const classNames: string[] = [];
  if (isDragAccept) classNames.push('drag-accept');
  if (isDragActive) classNames.push('drag-active');
  if (isDragReject) classNames.push('drag-reject');
  return classNames.join(' ');
}
